import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { ConnectorDetailsInterface } from "@/types/connector";

export interface ConnectorDetailsParams {
  evseID: string;
}

const fetchConnectorDetails = async (params: ConnectorDetailsParams, signal?: AbortSignal): Promise<ConnectorDetailsInterface> => {
  const response = await axios.get(`/api/evse/${params.evseID}/get-connector-details/`, { signal });
  return response.data as ConnectorDetailsInterface;
};

type QueryKeyType = readonly ["connector", "details", ConnectorDetailsParams | "disabled"];

export const connectorDetailsKeys = {
  all: ["connector"] as const,
  details: () => [...connectorDetailsKeys.all, "details"] as const,
  detail: (params: ConnectorDetailsParams | "disabled") => [...connectorDetailsKeys.details(), params] as const
};

export const useConnectorDetails = (
  params: ConnectorDetailsParams | null,
  options?: Omit<UseQueryOptions<ConnectorDetailsInterface, Error, ConnectorDetailsInterface, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? connectorDetailsKeys.detail(params) : connectorDetailsKeys.detail("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("Connector details params are required");
      }
      return fetchConnectorDetails(params, signal);
    },
    staleTime: 10 * 1000, // 10 seconds, connector status changes often
    cacheTime: 60 * 1000, // 1 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
